import {
  NextPageParams,
  PaginationMetadata,
  type CreateNotePayload,
  type Note,
  type NoteVersionResponse,
  type NoteVersionsResponse,
  type UpdateNotePayload,
} from "@/schema"

import { ApiContext, apiContextFetch } from "@/hooks/api/base"

export async function createNote(
  context: ApiContext,
  note: CreateNotePayload,
  newFiles: File[] = [],
  overrideBookId?: string
) {
  const apiRoot = (process.env.NEXT_PUBLIC_API_URL || "http://localhost:3000/api/").replace("/api/", "")
  const { uploadFileDirectly } = await import("@/utils/direct-upload")

  const newSignedIds = await Promise.all(newFiles.map((file) => uploadFileDirectly(file, apiRoot, context.token)))
  const files = [...(note.files ?? []), ...newSignedIds]

  return apiContextFetch<Note>(context, `notes`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
    },
    body: JSON.stringify({
      note: { ...note, files },
      ...(overrideBookId ? { override_book_id: overrideBookId } : {}),
    }),
  })
}

export async function updateNote(context: ApiContext, note: UpdateNotePayload, newFiles: File[] = []) {
  const apiRoot = (process.env.NEXT_PUBLIC_API_URL || "http://localhost:3000/api/").replace("/api/", "")
  const { uploadFileDirectly } = await import("@/utils/direct-upload")

  const newSignedIds = await Promise.all(newFiles.map((file) => uploadFileDirectly(file, apiRoot, context.token)))

  // Only send `files` when the caller explicitly supplied attachment data. Metadata-only
  // updates (e.g. toggling pinned/hidden/locked) omit files, and sending `files: []` would
  // detach every uploaded file. `undefined` is dropped by JSON.stringify, leaving them untouched.
  const files =
    note.files !== undefined || newSignedIds.length > 0 ? [...(note.files ?? []), ...newSignedIds] : undefined

  return apiContextFetch<Note>(context, `notes/${note.id}`, {
    method: "PATCH",
    headers: {
      "Content-Type": "application/json",
    },
    body: JSON.stringify({
      note: {
        content: note.content,
        hidden: note.hidden,
        pinned: note.pinned,
        locked: note.locked,
        mentions: note.mentions,
        tag_ids: note.tag_ids,
        files,
        links: note.links,
      },
    }),
  })
}

export function fetchNote(context: ApiContext, id: string) {
  return apiContextFetch<Note>(context, `notes/${id}`)
}

export function fetchNoteVersions(context: ApiContext, noteId: string, nextPageParams?: NextPageParams) {
  const params = new URLSearchParams()
  if (nextPageParams) {
    params.set("page", nextPageParams.page.toString())
    params.set("per_page", nextPageParams.per_page.toString())
  }

  const query = params.toString()
  return apiContextFetch<NoteVersionsResponse>(context, `notes/${noteId}/versions${query ? `?${query}` : ""}`)
}

export function fetchNoteVersion(context: ApiContext, noteId: string, versionId: string) {
  return apiContextFetch<NoteVersionResponse>(context, `notes/${noteId}/versions/${versionId}`)
}

export function restoreNoteVersion(context: ApiContext, noteId: string, versionId: string) {
  return apiContextFetch<Note>(context, `notes/${noteId}/version_reverts`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
    },
    body: JSON.stringify({ version_id: versionId }),
  })
}

export interface NotesResponse {
  notes: Note[]
  pagination: PaginationMetadata
}

export function fetchHouseholdNotes(
  context: ApiContext,
  householdId: string,
  search?: string,
  nextPageParams?: NextPageParams,
  showHiddenNotes?: boolean,
  tagIds?: string[]
) {
  const params = new URLSearchParams()

  if (search) {
    params.append("search", search)
  }

  if (nextPageParams) {
    params.append("page", nextPageParams.page.toString())
    params.append("per_page", nextPageParams.per_page.toString())
  }

  if (showHiddenNotes) {
    params.append("show_hidden", "true")
  }

  tagIds?.forEach((id) => params.append("tag_ids[]", id))

  const queryString = params.toString()
  const url = `households/${householdId}/notes${queryString ? `?${queryString}` : ""}`

  return apiContextFetch<NotesResponse>(context, url)
}

export function fetchContactNotes(
  context: ApiContext,
  contactId: string,
  search?: string,
  nextPageParams?: NextPageParams,
  showHiddenNotes?: boolean,
  tagIds?: string[]
) {
  const params = new URLSearchParams()

  if (search) {
    params.append("search", search)
  }

  if (nextPageParams) {
    params.append("page", nextPageParams.page.toString())
    params.append("per_page", nextPageParams.per_page.toString())
  }

  if (showHiddenNotes) {
    params.append("show_hidden", "true")
  }

  tagIds?.forEach((id) => params.append("tag_ids[]", id))

  const queryString = params.toString()
  const url = `contacts/${contactId}/notes${queryString ? `?${queryString}` : ""}`

  return apiContextFetch<NotesResponse>(context, url)
}

export function fetchBusinessNotes(
  context: ApiContext,
  businessId: string,
  search?: string,
  nextPageParams?: NextPageParams,
  showHiddenNotes?: boolean,
  tagIds?: string[]
) {
  const params = new URLSearchParams()

  if (search) {
    params.append("search", search)
  }

  if (nextPageParams) {
    params.append("page", nextPageParams.page.toString())
    params.append("per_page", nextPageParams.per_page.toString())
  }

  if (showHiddenNotes) {
    params.append("show_hidden", "true")
  }

  tagIds?.forEach((id) => params.append("tag_ids[]", id))

  const queryString = params.toString()
  const url = `businesses/${businessId}/notes${queryString ? `?${queryString}` : ""}`

  return apiContextFetch<NotesResponse>(context, url)
}

export function fetchTrustNotes(
  context: ApiContext,
  trustId: string,
  search?: string,
  nextPageParams?: NextPageParams,
  showHiddenNotes?: boolean,
  tagIds?: string[]
) {
  const params = new URLSearchParams()

  if (search) {
    params.append("search", search)
  }

  if (nextPageParams) {
    params.append("page", nextPageParams.page.toString())
    params.append("per_page", nextPageParams.per_page.toString())
  }

  if (showHiddenNotes) {
    params.append("show_hidden", "true")
  }

  tagIds?.forEach((id) => params.append("tag_ids[]", id))

  const queryString = params.toString()
  const url = `trusts/${trustId}/notes${queryString ? `?${queryString}` : ""}`

  return apiContextFetch<NotesResponse>(context, url)
}

export function fetchPersonNotes(
  context: ApiContext,
  personId: string,
  search?: string,
  nextPageParams?: NextPageParams,
  showHiddenNotes?: boolean,
  tagIds?: string[]
) {
  const params = new URLSearchParams()

  if (search) {
    params.append("search", search)
  }

  if (nextPageParams) {
    params.append("page", nextPageParams.page.toString())
    params.append("per_page", nextPageParams.per_page.toString())
  }

  if (showHiddenNotes) {
    params.append("show_hidden", "true")
  }

  tagIds?.forEach((id) => params.append("tag_ids[]", id))

  const queryString = params.toString()
  const url = `people/${personId}/notes${queryString ? `?${queryString}` : ""}`

  return apiContextFetch<NotesResponse>(context, url)
}

export function deleteNote(context: ApiContext, id: string) {
  return apiContextFetch<void>(context, `notes/${id}`, {
    method: "DELETE",
  })
}

export function bulkDeleteNotes(context: ApiContext, ids: string[]) {
  return apiContextFetch<void>(context, "notes/bulk_deletions", {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
    },
    body: JSON.stringify({ note_ids: ids }),
  })
}

export function bulkAddNoteTag(context: ApiContext, notes: Note[], tagId: string) {
  return apiContextFetch<{ updated_ids: string[] }>(context, "notes/bulk_labelings", {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
    },
    body: JSON.stringify({
      note_ids: notes.flatMap((note) => (note.id ? [note.id] : [])),
      tag_id: tagId,
    }),
  })
}

// Silently sync HTML-to-nodes conversion without updating timestamps
export function syncNoteNodes(context: ApiContext, noteId: string, nodes: Record<string, unknown>) {
  return apiContextFetch<void>(context, `notes/${noteId}/sync_nodes`, {
    method: "PATCH",
    headers: {
      "Content-Type": "application/json",
    },
    body: JSON.stringify({ content: { nodes } }),
  })
}

export interface BulkCreateNotesPayload {
  file_name?: string
  records: Array<{
    notable_type: string
    notable_id?: string
    notable_name?: string
    content: string
    original_created_at?: string
  }>
}

export interface BulkCreateNotesResponse {
  list_id: string
}

export function bulkCreateNotes(context: ApiContext, params: BulkCreateNotesPayload) {
  return apiContextFetch<BulkCreateNotesResponse>(context, "notes/bulk_create", {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
    },
    body: JSON.stringify(params),
  })
}
